package info.rezident.app.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.borderColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import info.rezident.app.core.AppTracking
import info.rezident.app.core.Logger
import info.rezident.app.data.DependencyInjection
import info.rezident.app.data.quiz.UserQuizRepository
import info.rezident.app.seo.*
import info.rezident.app.ui.components.sections.NavigationBar
import info.rezident.app.ui.components.sections.QuizHeader
import info.rezident.app.ui.components.sections.QuizHeaderSpacer
import info.rezident.app.ui.components.sections.StepScaffold
import info.rezident.app.ui.theme.AppTheme
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

@Page
@Composable
fun QuizPage() {
    val title = "Începe Quiz-ul de Orientare pentru Rezidențiat 2026"
    val description =
        "Răspunde la câteva întrebări de personalitate, preferințe și abilități, și primești Recomandări de Specialități, cu Scor de Potrivire, Puncte Forte/Slabe și Analiză Detaliată."
    val canonicalUrl = "https://rezident.info/quiz"
    val ogImageUrl = "https://rezident.info/assets/og/og-image.png"

    Seo(title = title, description = description)

    LaunchedEffect(Unit) {
        document.setCanonicalUrl(canonicalUrl)
        document.setMetaNameTag(name = "robots", content = "index, follow")
        document.setMetaNameTag(name = "twitter:card", content = "summary_large_image")
        document.setMetaNameTag(name = "twitter:title", content = title)
        document.setMetaNameTag(name = "twitter:description", content = description)
        document.setMetaNameTag(name = "twitter:image", content = ogImageUrl)

        document.setOpenGraphTag(property = "og:title", content = title)
        document.setOpenGraphTag(property = "og:description", content = description)
        document.setOpenGraphTag(property = "og:url", content = canonicalUrl)
        document.setOpenGraphTag(property = "og:image", content = ogImageUrl)
    }

    val quizJsonLd: String = """
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Începe Quiz-ul de Orientare pentru Rezidențiat 2026",
          "url": "https://rezident.info/quiz",
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Web",
          "inLanguage": "ro-RO",
          "offers": [
            {
              "@type": "Offer",
              "price": "0.00",
              "priceCurrency": "RON",
              "category": "Free",
              "name": "Recomandările Tale"
            }
          ]
        }
    """.trimIndent()

    ApplicationLdJson(quizJsonLd)

    val viewModel = remember { DependencyInjection.quizViewModel }
    val breakpoint = rememberBreakpoint()

    val isLoading by viewModel.isLoading.collectAsState()
    val steps by viewModel.steps.collectAsState()
    val questions by viewModel.questions.collectAsState()
    val userAnswers by UserQuizRepository.userAnswers.collectAsState()

    // Add transition states
    var isTransitioning by remember { mutableStateOf(false) }

    val currentStepId by viewModel.currentStepId.collectAsState()
    var nextStepId by remember { mutableStateOf(currentStepId + 1) }

    // Track if current step is completed (all mandatory questions answered)
    var stepCompleted by remember { mutableStateOf(false) }

    // Track quiz start time for analytics
    var quizStartTime by remember { mutableStateOf<Long?>(null) }
    var hasTrackedQuizStarted by remember { mutableStateOf(false) }
    var hasTrackedQuizCompleted by remember { mutableStateOf(false) }

    // Track quiz started event
    LaunchedEffect(isLoading) {
        if (!isLoading && !hasTrackedQuizStarted) {
            quizStartTime = Clock.System.now().toEpochMilliseconds()
            hasTrackedQuizStarted = true
            AppTracking.logEvent(
                AppTracking.EVENT_QUIZ_STARTED,
                mapOf(AppTracking.PARAM_TEMPLATE_ID to userAnswers.templateId.toString())
            )
        }
    }

    // Function to handle the transition to a new step
    fun transitionToStep(step: Int) {
        if (!isTransitioning) {
            // Save current step answers to the ViewModel before transitioning
            isTransitioning = true

            window.setTimeout(
                {
                    viewModel.updateCurrentStep(step)
                    nextStepId = step + 1

                    window.setTimeout(
                        {
                            window.scrollTo(
                                js("{ top: 0, left: 0, behavior: 'smooth' }")
                            )
                        }, 50
                    )

                    isTransitioning = false
                },
                300
            )
        }
    }

    // Navigation handlers
    val handlePreviousStep = {
        if (currentStepId > (steps.minByOrNull { it.id }?.id ?: 1) && !isTransitioning) {
            transitionToStep(currentStepId - 1)
        }
    }

    val scope = rememberCoroutineScope()
    val pageContext = rememberPageContext()
    val handleNextStep = {
        if (currentStepId < 4 && !isTransitioning) {
            transitionToStep(currentStepId + 1)
        } else if (currentStepId == 4) {
            // For Summary step, wait for saving to complete before navigation
            scope.launch {
                try {
                    val quizDocumentId = viewModel.waitForQuizSavingAndGetDocumentId()
                    if (quizDocumentId != null) {
                        // Analytics: navigation from quiz to recommendations
                        AppTracking.logEvent(
                            AppTracking.EVENT_QUIZ_RECOMMENDATIONS_CLICK,
                            mapOf(AppTracking.PARAM_TEMPLATE_ID to userAnswers.templateId)
                        )
                        pageContext.router.navigateTo("/recomandari?quizId=$quizDocumentId")
                    } else {
                        Logger.error("Failed to get quiz document ID, not navigating to recommendations")
                        // Don't navigate if we don't have a document ID
                    }
                } catch (e: Exception) {
                    Logger.error("Error waiting for quiz saving: ${e.message}")
                    // Don't navigate on error
                }
            }
        }
    }

    // Check if current step is completed whenever answers or currentStepId changes
    LaunchedEffect(userAnswers.answers, currentStepId) {
        stepCompleted = viewModel.isStepCompleted(currentStepId)

        // Log the Quiz completed event when navigating to summary step
        if (currentStepId == 4) {
            if (!hasTrackedQuizCompleted && quizStartTime != null) {
                hasTrackedQuizCompleted = true
                val timeSpentSeconds = (Clock.System.now().toEpochMilliseconds() - (quizStartTime ?: 0L)) / 1000
                AppTracking.logEvent(
                    AppTracking.EVENT_QUIZ_COMPLETED,
                    mapOf(
                        AppTracking.PARAM_TEMPLATE_ID to userAnswers.templateId.toString(),
                        AppTracking.PARAM_TIME_SPENT_S to timeSpentSeconds.toString()
                    )
                )
            }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .backgroundColor(AppTheme.colors.background),
        contentAlignment = Alignment.TopCenter
    ) {
        if (isLoading) {
            QuizHeader(
                currentStep = 1,
                totalSteps = 4,
                nextStepTitle = null,
            )

            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .backgroundColor(AppTheme.colors.background),
                contentAlignment = Alignment.Center
            ) {
                LoadingSpinner()
            }
        } else {
            QuizHeader(
                currentStep = currentStepId,
                totalSteps = steps.size + 1,
                nextStepTitle = viewModel.getStepTitle(nextStepId),
            )

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .backgroundColor(AppTheme.colors.background)
                    .padding(
                        left = if (breakpoint < Breakpoint.MD) 16.px else 32.px,
                        top = 16.px,
                        right = if (breakpoint < Breakpoint.MD) 16.px else 32.px,
                        bottom = 32.px,
                    )
                    .thenIf(
                        breakpoint >= Breakpoint.MD,
                        Modifier.maxWidth(1200.px)
                    ),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Add spacer for the fixed header
                QuizHeaderSpacer()

                // Remove AI recommendations trigger since we're not using step 6 anymore

                // Quiz content with transition effect
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .backgroundColor(AppTheme.colors.background)
                        .margin(bottom = if (breakpoint < Breakpoint.MD) 80.px else 48.px)
                        .opacity(if (isTransitioning) 0 else 1)
                        .styleModifier {
                            property("transition", "opacity 0.3s ease-in-out")
                        },
                    horizontalAlignment = Alignment.Start
                ) {
                    when {
                        currentStepId <= 3 -> {
                            val step = steps.firstOrNull { it.id == currentStepId }
                            val stepQuestions = questions.filter { it.stepId == currentStepId }

                            if (step != null) {
                                StepScaffold(
                                    quizTitle = if (currentStepId == 1) {
                                        "Quiz de Orientare"
                                    } else null,
                                    stepTitle = step.title,
                                    description = step.subtitle,
                                    questions = stepQuestions,
                                    userAnswers.answers
                                ) { newAnswer ->
                                    viewModel.updateAnswers(newAnswer)
                                }
                            }
                        }

                        currentStepId == 4 -> {
                            SummaryStep()
                        }
                    }
                }
            }
        }
    }

    if (!isLoading) {
        val isSavingQuiz by viewModel.isSavingQuiz.collectAsState()

        NavigationBar(
            currentStep = currentStepId,
            canGoBack = currentStepId > 1,
            isLastStep = false, // Never hide the next button since Summary leads to Recommendations
            isTransitioning = isTransitioning,
            canGoForward = (stepCompleted || currentStepId > 3) && !(currentStepId == 4 && isSavingQuiz),
            nextLabel = if (currentStepId == 4) {
                if (isSavingQuiz) "Se procesează..." else "Află specialitățile"
            } else null,
            isLoading = currentStepId == 4 && isSavingQuiz,
            onBackClick = handlePreviousStep,
            onContinueClick = handleNextStep
        )
    }
}

val spin = Keyframes {
    from {
        Modifier.rotate(0.deg)
    }

    to {
        Modifier.rotate(360.deg)
    }
}

@Composable
private fun LoadingSpinner() {
    val spinnerColor = AppTheme.colors.loading
    Div(
        attrs = {
            style {
                border(4.px, LineStyle.Solid)
                borderColor(top = Color.transparent, leftRight = spinnerColor, bottom = spinnerColor)
                borderRadius(50.percent)
                width(48.px)
                height(48.px)
                property("animation", "spin 1s linear infinite")
            }
        }
    )
}